import React, { useEffect, useState, useCallback } from 'react';
import { drivers as driversApi, extractError } from '../services/api';
import { DriverStatusBadge } from '../components/ui/StatusBadge';
import { Modal, ConfirmDialog } from '../components/ui/Modal';
import { TableSkeleton } from '../components/ui/LoadingSpinner';
import { useToast } from '../store/ToastContext';
import { useAuth } from '../store/AuthContext';

const CDL_CLASSES = ['A', 'B', 'C'];
const HOS_CYCLES  = [{ value: '8_day_70hr', label: '8-Day / 70 Hour' }, { value: '7_day_60hr', label: '7-Day / 60 Hour' }];
const STATUSES    = ['active', 'inactive', 'on_leave', 'terminated'];

const BLANK = {
  first_name: '', last_name: '', email: '', password: '',
  cdl_number: '', cdl_state: '', cdl_expiry: '', cdl_class: 'A',
  hire_date: '', status: 'active', current_hos_cycle: '8_day_70hr',
  medical_card_expiry: '', notes: '',
};

function DriverForm({ initial, onSave, onClose, isEdit }) {
  const toast = useToast();
  const [form, setForm] = useState({ ...BLANK, ...initial });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); if (errors[k]) setErrors(e => ({...e,[k]:undefined})); };

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = 'Required';
    if (!form.last_name.trim())  e.last_name  = 'Required';
    if (!isEdit && !form.email.trim()) e.email = 'Required';
    if (!isEdit && !form.password)     e.password = 'Required';
    if (!form.cdl_number.trim()) e.cdl_number = 'Required';
    if (!form.cdl_state.trim())  e.cdl_state  = 'Required (2-letter)';
    if (!form.cdl_expiry)        e.cdl_expiry = 'Required';
    if (!form.hire_date)         e.hire_date  = 'Required';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave(form);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">First Name *</label>
          <input className={`input-field ${errors.first_name ? 'input-error' : ''}`} value={form.first_name} onChange={e => set('first_name', e.target.value)} />
          {errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>}
        </div>
        <div>
          <label className="label">Last Name *</label>
          <input className={`input-field ${errors.last_name ? 'input-error' : ''}`} value={form.last_name} onChange={e => set('last_name', e.target.value)} />
          {errors.last_name && <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>}
        </div>
      </div>
      {!isEdit && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Email *</label>
            <input type="email" className={`input-field ${errors.email ? 'input-error' : ''}`} value={form.email} onChange={e => set('email', e.target.value)} />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>
          <div>
            <label className="label">Password *</label>
            <input type="password" className={`input-field ${errors.password ? 'input-error' : ''}`} value={form.password} onChange={e => set('password', e.target.value)} placeholder="Min 8 characters" />
            {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
          </div>
        </div>
      )}
      {isEdit && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Email</label>
            <input type="email" className="input-field" value={form.email} onChange={e => set('email', e.target.value)} />
          </div>
          <div>
            <label className="label">New Password <span className="text-gray-400 text-xs">(leave blank to keep)</span></label>
            <input type="password" className="input-field" value={form.password || ''} onChange={e => set('password', e.target.value)} placeholder="Leave blank to keep" />
          </div>
        </div>
      )}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="label">CDL Number *</label>
          <input className={`input-field ${errors.cdl_number ? 'input-error' : ''}`} value={form.cdl_number} onChange={e => set('cdl_number', e.target.value)} placeholder="TX12345678" />
          {errors.cdl_number && <p className="text-red-500 text-xs mt-1">{errors.cdl_number}</p>}
        </div>
        <div>
          <label className="label">CDL State *</label>
          <input maxLength={2} className={`input-field ${errors.cdl_state ? 'input-error' : ''}`} value={form.cdl_state} onChange={e => set('cdl_state', e.target.value.toUpperCase())} placeholder="TX" />
          {errors.cdl_state && <p className="text-red-500 text-xs mt-1">{errors.cdl_state}</p>}
        </div>
        <div>
          <label className="label">CDL Class *</label>
          <select className="input-field" value={form.cdl_class} onChange={e => set('cdl_class', e.target.value)}>
            {CDL_CLASSES.map(c => <option key={c} value={c}>Class {c}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="label">CDL Expiry *</label>
          <input type="date" className={`input-field ${errors.cdl_expiry ? 'input-error' : ''}`} value={form.cdl_expiry} onChange={e => set('cdl_expiry', e.target.value)} />
          {errors.cdl_expiry && <p className="text-red-500 text-xs mt-1">{errors.cdl_expiry}</p>}
        </div>
        <div>
          <label className="label">Hire Date *</label>
          <input type="date" className={`input-field ${errors.hire_date ? 'input-error' : ''}`} value={form.hire_date} onChange={e => set('hire_date', e.target.value)} />
          {errors.hire_date && <p className="text-red-500 text-xs mt-1">{errors.hire_date}</p>}
        </div>
        <div>
          <label className="label">Medical Card Expiry</label>
          <input type="date" className="input-field" value={form.medical_card_expiry || ''} onChange={e => set('medical_card_expiry', e.target.value)} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Status</label>
          <select className="input-field" value={form.status} onChange={e => set('status', e.target.value)}>
            {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
          </select>
        </div>
        <div>
          <label className="label">HOS Cycle</label>
          <select className="input-field" value={form.current_hos_cycle} onChange={e => set('current_hos_cycle', e.target.value)}>
            {HOS_CYCLES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="label">Notes</label>
        <textarea rows={2} className="input-field resize-none" value={form.notes || ''} onChange={e => set('notes', e.target.value)} />
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="btn-secondary">Cancel</button>
        <button onClick={handleSave} disabled={saving} className="btn-primary">
          {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Driver'}
        </button>
      </div>
    </div>
  );
}

export function DriversPage() {
  const { hasRole } = useAuth();
  const toast = useToast();
  const [driverList, setList]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [statusFil, setStatus]  = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editDriver, setEdit]   = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const canManage = hasRole(['administrator', 'fleet_manager']);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await driversApi.list(statusFil !== 'all' ? { status: statusFil } : {});
      setList(Array.isArray(result) ? result : result?.data || []);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFil]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (form) => {
    const d = await driversApi.create(form);
    setList(prev => [d, ...prev]);
    setShowForm(false);
    toast.success(`Driver ${d.full_name || d.first_name + ' ' + d.last_name} added`);
  };

  const handleUpdate = async (form) => {
    const d = await driversApi.update(editDriver.id, form);
    setList(prev => prev.map(x => x.id === d.id ? d : x));
    setEdit(null);
    toast.success('Driver updated');
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await driversApi.delete(confirmDel.id);
      setList(prev => prev.filter(d => d.id !== confirmDel.id));
      toast.success('Driver deactivated');
      setConfirmDel(null);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setDeleting(false);
    }
  };

  const filtered = driverList.filter(d => {
    if (statusFil !== 'all' && d.status !== statusFil) return false;
    if (search) {
      const s = search.toLowerCase();
      return (d.full_name || `${d.first_name} ${d.last_name}`).toLowerCase().includes(s) ||
             d.email?.toLowerCase().includes(s) ||
             d.cdl_number?.toLowerCase().includes(s);
    }
    return true;
  });

  const today = new Date().toISOString().split('T')[0];
  const expirySoon = (date) => {
    if (!date) return false;
    const diff = (new Date(date) - new Date()) / (1000*60*60*24);
    return diff >= 0 && diff <= 90;
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Drivers</h1>
            <p className="text-sm text-gray-500">{filtered.length} driver{filtered.length !== 1 ? 's' : ''}</p>
          </div>
          {canManage && (
            <button onClick={() => setShowForm(true)} className="btn-primary">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Driver
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
        <div className="flex gap-3">
          <div className="relative flex-1 max-w-sm">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input type="text" className="input-field pl-9 py-2" placeholder="Search drivers…"
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="flex gap-1">
            {['all', ...STATUSES].map(s => (
              <button key={s} onClick={() => setStatus(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${statusFil === s ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {s === 'all' ? 'All' : s.replace(/_/g,' ')}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-6 py-4">
        <div className="card overflow-hidden">
          {loading ? <TableSkeleton rows={6} cols={7} /> : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <p className="text-sm">No drivers found</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="table-header">Driver</th>
                  <th className="table-header">CDL</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">CDL Expiry</th>
                  <th className="table-header">Hire Date</th>
                  <th className="table-header">HOS Cycle</th>
                  {canManage && <th className="table-header">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(d => (
                  <tr key={d.id} className="hover:bg-gray-50 transition-colors">
                    <td className="table-cell">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {(d.first_name?.[0] || '') + (d.last_name?.[0] || '')}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{d.full_name || `${d.first_name} ${d.last_name}`}</div>
                          <div className="text-xs text-gray-500">{d.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="font-medium">{d.cdl_number}</div>
                      <div className="text-xs text-gray-500">{d.cdl_state} · Class {d.cdl_class}</div>
                    </td>
                    <td className="table-cell"><DriverStatusBadge status={d.status} /></td>
                    <td className={`table-cell ${expirySoon(d.cdl_expiry) ? 'text-amber-600 font-medium' : ''}`}>
                      {d.cdl_expiry ? new Date(d.cdl_expiry).toLocaleDateString() : '—'}
                      {expirySoon(d.cdl_expiry) && <span className="text-xs block">⚠ Expiring soon</span>}
                    </td>
                    <td className="table-cell">{d.hire_date ? new Date(d.hire_date).toLocaleDateString() : '—'}</td>
                    <td className="table-cell text-xs">{d.current_hos_cycle?.replace(/_/g,' ')}</td>
                    {canManage && (
                      <td className="table-cell">
                        <div className="flex gap-1">
                          <button onClick={() => setEdit(d)} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 font-medium transition-colors">Edit</button>
                          <button onClick={() => setConfirmDel(d)} className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-medium transition-colors">Deactivate</button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add driver modal */}
      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title="Add New Driver" size="lg">
        <DriverForm initial={BLANK} onSave={handleCreate} onClose={() => setShowForm(false)} isEdit={false} />
      </Modal>

      {/* Edit driver modal */}
      {editDriver && (
        <Modal isOpen onClose={() => setEdit(null)} title="Edit Driver" size="lg">
          <DriverForm initial={editDriver} onSave={handleUpdate} onClose={() => setEdit(null)} isEdit />
        </Modal>
      )}

      {/* Confirm deactivation */}
      <ConfirmDialog
        isOpen={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={handleDelete}
        title="Deactivate Driver"
        message={`Deactivate ${confirmDel?.full_name || confirmDel?.first_name + ' ' + confirmDel?.last_name}? They will be marked as terminated and cannot log in.`}
        confirmText="Deactivate"
        variant="danger"
        isLoading={deleting}
      />
    </div>
  );
}
